import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Download, FileText, Users } from 'lucide-react';

function CustomerLedgerTab({ dateRange }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLedger, setCustomerLedger] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer && dateRange?.start && dateRange?.end) {
      fetchCustomerLedger(selectedCustomer);
    }
  }, [selectedCustomer, dateRange]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data.customers);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const fetchCustomerLedger = async (customerId) => {
    setSelectedCustomer(customerId);
    try {
      const res = await axios.get(`${API}/customer-ledger/${customerId}?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setCustomerLedger(res.data);
    } catch (error) {
      toast.error('Failed to load customer ledger');
    }
  };

  const exportCustomerLedgerPDF = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await axios.get(
        `${API}/reports/customer-ledger-pdf/${selectedCustomer}?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Find customer name for the filename
      const customer = customers.find(c => c.id === selectedCustomer);
      const fileName = customer ? `${customer.firm_name.replace(/\s+/g, '_')}_Ledger.pdf` : 'customer_ledger.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customer ledger downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-ledger-tab">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="font-medium">Customer Ledger</h4>
          <p className="text-sm text-muted">View customer transactions and balances</p>
        </div>
        <div className="flex gap-2">
          {selectedCustomer && (
            <Button variant="outline" onClick={exportCustomerLedgerPDF} data-testid="export-customer-ledger-pdf">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {/* Customer List */}
        <Card className="border border-border p-4 md:col-span-1 max-h-[600px] overflow-y-auto">
          <h3 className="font-semibold mb-4">Select Customer</h3>
          {customers.length === 0 ? (
            <div className="text-center text-muted py-4">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No customers found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customers.map(c => (
                <button
                  key={c.id}
                  onClick={() => fetchCustomerLedger(c.id)}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${
                    selectedCustomer === c.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary'
                  }`}
                  data-testid={`customer-select-${c.id}`}
                >
                  <p className="font-medium text-sm">{c.firm_name}</p>
                  <p className="text-xs text-muted">{c.customer_name}</p>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Customer Ledger Details */}
        <div className="md:col-span-3">
          {customerLedger ? (
            <>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <Card className="p-4 border border-border">
                  <p className="text-xs text-muted">Customer</p>
                  <p className="font-bold">{customerLedger.customer?.firm_name}</p>
                </Card>
                <Card className="p-4 border border-border">
                  <p className="text-xs text-muted">Credit Limit</p>
                  <p className="font-bold">₹{customerLedger.credit_limit?.toLocaleString('en-IN')}</p>
                </Card>
                <Card className="p-4 border border-border">
                  <p className="text-xs text-muted">Current Balance</p>
                  <p className={`font-bold ${customerLedger.current_balance > customerLedger.credit_limit ? 'text-red-600' : ''}`}>
                    ₹{customerLedger.current_balance?.toLocaleString('en-IN')}
                  </p>
                </Card>
                <Card className="p-4 border border-border">
                  <p className="text-xs text-muted">Available Credit</p>
                  <p className={`font-bold ${customerLedger.credit_limit - customerLedger.current_balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₹{(customerLedger.credit_limit - customerLedger.current_balance)?.toLocaleString('en-IN')}
                  </p>
                </Card>
              </div>

              <div className="border border-border rounded-lg overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Debit</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Credit</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {customerLedger.entries?.map((entry) => (
                      <tr key={entry.id} className="hover:bg-secondary/50">
                        <td className="px-4 py-3 text-sm">{new Date(entry.date).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            entry.entry_type === 'payment_credit' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                            entry.entry_type === 'fuel_purchase' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                            entry.entry_type === 'discount' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {entry.entry_type?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{entry.description}</td>
                        <td className="px-4 py-3 text-sm text-right">{entry.debit > 0 ? `₹${entry.debit.toLocaleString('en-IN')}` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">{entry.credit > 0 ? `₹${entry.credit.toLocaleString('en-IN')}` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">₹{entry.balance?.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customerLedger.entries?.length === 0 && (
                  <div className="text-center py-8 text-muted">
                    <p>No transactions found for this period</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Card className="p-12 border border-border text-center">
              <FileText className="w-12 h-12 mx-auto text-muted mb-4" />
              <p className="text-muted">Select a customer to view their ledger</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerLedgerTab;
